import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { Configuration } from "../../assets/Configuration";
import { PersonIddProgressModel } from "../models/dashboard/person-idd-progress.model";
import { EmployeesIddProgressModel } from "../models/dashboard/employees-idd-progress.model";
import { TrainingGroupIddProgressModel } from "../models/dashboard/training-groups-idd-progress.model";
import { ComplianceCourseProgressModel } from '../models/dashboard/compliance-course-progress.model';
import { QualificationStatusPersonPerMonthModel } from '../models/dashboard/qualification-status-person-per-month.model';
import { DashboardCompletedEventCategoryModel } from '../models/completed-event/dashboard-completed-event-category.model';
import { LearningPathProgressModel } from '../models/dashboard/learning-path-progress.model';
import { TopRecordsModel } from '../models/top-records/top-records.model';
import { DashboardComparisonStatisticsModel } from '../models/statistics/dashboard-comparison-statistics.model';
import { ChannelProgressModel } from '../models/dashboard/channel-progress.model';
import { PendingRequestSummaryModel } from '../models/dashboard/pending-request-summary.model';
import { LearningRecommendationModel } from '../models/learning-recommendations/learning-recommendation.model';

@Injectable({
  providedIn: 'root'
})
export class DashboardService {

  constructor(private httpClient: HttpClient) {}

  public getDashboardComparisonStatistics(
    startDate: Date, endDate: Date, organisationUnitIds: string[]
  ): Observable<DashboardComparisonStatisticsModel> {
    const body = this.getDateRangeBody(startDate, endDate, organisationUnitIds);

    return this.httpClient.post<DashboardComparisonStatisticsModel>(
      Configuration.getApiBaseUrl() + `/dashboard/statistics`,
      body
    );
  }

  public getTop10Records(
    startDate: Date, endDate: Date, organisationUnitIds: string[]
  ): Observable<TopRecordsModel> {
    const body = this.getDateRangeBody(startDate, endDate, organisationUnitIds);

    return this.httpClient.post<TopRecordsModel>(
      Configuration.getApiBaseUrl() + `/dashboard/top-10`,
      body
    );
  }

  public getChannelProgress(
    startDate: Date, endDate: Date, organisationUnitIds: string[]
  ): Observable<ChannelProgressModel[]> {
    const body = this.getDateRangeBody(startDate, endDate, organisationUnitIds);

    return this.httpClient.post<ChannelProgressModel[]>(
      Configuration.getApiBaseUrl() + `/dashboard/channel-progress`,
      body
    );
  }

  public getPersonProgress(
    startDate: Date, endDate: Date
  ): Observable<PersonIddProgressModel> {
    const params = new HttpParams()
      .set('start', startDate.toDateString())
      .set('end', endDate.toDateString());

    return this.httpClient.get<PersonIddProgressModel>(
      Configuration.getApiBaseUrl() + `/dashboard/qualification-status-person`,
      { params }
    );
  }

  public getEmployeesProgress(
    startDate: Date, endDate: Date, organisationUnitIds: string[]
  ): Observable<EmployeesIddProgressModel> {
    const body = this.getDateRangeBody(startDate, endDate, organisationUnitIds);

    return this.httpClient.post<EmployeesIddProgressModel>(
      Configuration.getApiBaseUrl() + `/dashboard/qualification-status-employees`,
      body
    );
  }

  public getTrainingGroupsProgress(
    startDate: Date, endDate: Date, organisationUnitIds: string[]
  ): Observable<TrainingGroupIddProgressModel[]> {
    const body = this.getDateRangeBody(startDate, endDate, organisationUnitIds);

    return this.httpClient.post<TrainingGroupIddProgressModel[]>(
      Configuration.getApiBaseUrl() + `/dashboard/qualification-status-training-groups`,
      body
    );
  }

  public getLearningPathsProgress(
    organisationUnitIds: string[]
  ): Observable<LearningPathProgressModel[]> {
    const body = {
      organisationUnitIds
    };

    return this.httpClient.post<LearningPathProgressModel[]>(
      Configuration.getApiBaseUrl() + `/dashboard/learning-path-progress`,
      body
    );
  }

  public getComplianceCoursesProgress(
    startDate: Date, endDate: Date, organisationUnitIds: string[]
  ): Observable<ComplianceCourseProgressModel[]> {
    const body = this.getDateRangeBody(startDate, endDate, organisationUnitIds);

    return this.httpClient.post<ComplianceCourseProgressModel[]>(
      Configuration.getApiBaseUrl() + `/dashboard/qualification-status-compliance`,
      body
    );
  }

  public getQualificationStatusPerMonth(
    year: number
  ): Observable<QualificationStatusPersonPerMonthModel[]> {
    return this.httpClient.get<QualificationStatusPersonPerMonthModel[]>(
      Configuration.getApiBaseUrl() + `/dashboard/qualification-status-person-per-month/${year}`
    );
  }

  public getEventCompletedCategories(
    startDate: Date, endDate: Date, organisationUnitIds: string[]
  ): Observable<DashboardCompletedEventCategoryModel[]> {
    const body = this.getDateRangeBody(startDate, endDate, organisationUnitIds);

    return this.httpClient.post<DashboardCompletedEventCategoryModel[]>(
      Configuration.getApiBaseUrl() + `/dashboard/competence`,
      body
    );
  }

  public getPendingRequests(): Observable<PendingRequestSummaryModel[]> {
    return this.httpClient.get<PendingRequestSummaryModel[]>(
      Configuration.getApiBaseUrl() + `/dashboard/pending-requests/summary`
    );
  }

  public getLearningRecommendations(
    itemsCount: number = 4
  ): Observable<LearningRecommendationModel[]> {
    const params = new HttpParams()
      .set('itemsCount', itemsCount);

    return this.httpClient.get<LearningRecommendationModel[]>(
      Configuration.getApiBaseUrl() + `/dashboard/learning-recommendations`,
      { params }
    );
  }

  private getDateRangeBody(
    startDate: Date,
    endDate: Date,
    organisationUnitIds: string[]
  ) {
    return {
      start: this.formatDate(startDate),
      end: this.formatDate(endDate),
      organisationUnitIds
    };
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}